/**
 * Auto-layout utilities for Print Map Maker
 * 
 * Calculates optimal view positioning to encompass all locations
 * with appropriate buffer space and centering.
 */

import { GeoCoord, GeoBounds } from './geotiff';

/**
 * View state for map positioning
 */
export interface ViewState {
  centerLat: number;
  centerLong: number;
  zoomLevel: number;
  pixelOffsetX: number;
  pixelOffsetY: number;
}

/**
 * Physical dimensions of the map
 */
export interface PhysicalSize {
  width: number;
  height: number;
  unit: 'inches';
}

/**
 * Location with geographic coordinates
 */
export interface Location {
  lat: number;
  long: number;
}

/**
 * Calculate auto-layout view state for a set of locations
 * 
 * This function:
 * 1. Calculates the minimum bounding box containing all locations
 * 2. Adds 10% buffer space on all sides
 * 3. Centers the view within the physical map size
 * 4. Respects the aspect ratio of the physical map size
 * 
 * @param locations - Array of locations with lat/long coordinates
 * @param physicalSize - Physical dimensions of the map in inches
 * @returns ViewState object with center coordinates, zoom level, and offsets
 * 
 * @throws Error if locations array is empty
 * 
 * **Validates: Requirements 4.1, 4.2, 4.3, 4.4, 4.5, 4.6**
 */
export function calculateAutoLayout(
  locations: Location[],
  physicalSize: PhysicalSize
): ViewState {
  if (locations.length === 0) {
    throw new Error('Cannot calculate auto-layout for empty location list');
  }

  // Step 1: Calculate minimum bounding box containing all locations
  const bounds = calculateBoundingBox(locations);

  // Step 2: Add 10% buffer space on all sides
  const bufferedBounds = addBufferSpace(bounds, 0.10);

  // Step 3 & 4: Calculate zoom level and center position
  // that respects the physical map aspect ratio
  const viewState = calculateViewState(bufferedBounds, physicalSize);

  return viewState;
}

/**
 * Calculate the minimum bounding box containing all locations
 * 
 * @param locations - Array of locations
 * @returns Geographic bounds containing all locations
 */
function calculateBoundingBox(locations: Location[]): GeoBounds {
  let minLat = locations[0].lat;
  let maxLat = locations[0].lat;
  let minLong = locations[0].long;
  let maxLong = locations[0].long;

  for (const location of locations) {
    minLat = Math.min(minLat, location.lat);
    maxLat = Math.max(maxLat, location.lat);
    minLong = Math.min(minLong, location.long);
    maxLong = Math.max(maxLong, location.long);
  }

  return {
    north: maxLat,
    south: minLat,
    east: maxLong,
    west: minLong,
  };
}

/**
 * Add buffer space around geographic bounds
 * 
 * @param bounds - Original geographic bounds
 * @param bufferPercent - Buffer percentage (e.g., 0.10 for 10%)
 * @returns Bounds with buffer space added on all sides
 */
function addBufferSpace(bounds: GeoBounds, bufferPercent: number): GeoBounds {
  const latRange = bounds.north - bounds.south;
  const longRange = bounds.east - bounds.west;

  const latBuffer = latRange * bufferPercent;
  const longBuffer = longRange * bufferPercent;

  return {
    north: bounds.north + latBuffer,
    south: bounds.south - latBuffer,
    east: bounds.east + longBuffer,
    west: bounds.west - longBuffer,
  };
}

/**
 * Calculate view state that centers the bounds within the physical map size
 * while respecting the aspect ratio
 * 
 * @param bounds - Geographic bounds to fit
 * @param physicalSize - Physical dimensions of the map
 * @returns ViewState with center coordinates, zoom level, and offsets
 */
function calculateViewState(
  bounds: GeoBounds,
  physicalSize: PhysicalSize
): ViewState {
  // Calculate center of the bounds
  const centerLat = (bounds.north + bounds.south) / 2;
  const centerLong = (bounds.east + bounds.west) / 2;

  // Calculate the dimensions of the bounds in degrees
  const boundsWidth = bounds.east - bounds.west;
  const boundsHeight = bounds.north - bounds.south;

  // Handle edge case where bounds have zero width or height
  let zoomLevel: number;

  if (boundsWidth === 0 || boundsHeight === 0) {
    zoomLevel = 200; // Single location or line - zoom in moderately
  } else {
    // Calculate zoom level to fit the bounding box in the view
    // The US VFR Wall Planning Chart covers approximately:
    // - Longitude: -129.92° to -64.14° (65.78° total width)
    // - Latitude: 22.59° to 48.52° (25.93° total height)
    const fullMapWidth = 65.78;
    const fullMapHeight = 25.93;
    
    // Calculate what percentage of the full map our bounds occupy
    const widthRatio = boundsWidth / fullMapWidth;
    const heightRatio = boundsHeight / fullMapHeight;
    
    // Use the larger ratio (the dimension that needs more space)
    const maxRatio = Math.max(widthRatio, heightRatio);
    
    // Zoom level calculation:
    // At 100% zoom, the full map is visible
    // At 200% zoom, half the map is visible (zoomed in 2x)
    // 
    // We want to show the locations with good visibility and context.
    // Target: locations should fill about 40% of the viewport
    // This shows them clearly while still providing surrounding geography.
    // 
    // Example: If locations occupy 12% of map (with buffer):
    // - To make them fill 40% of viewport: 100 * (0.40 / 0.12) = 333%
    const viewportFillTarget = 0.40; // Locations should fill 40% of viewport
    zoomLevel = 100 * (viewportFillTarget / maxRatio);
    
    // Clamp zoom level to reasonable bounds (50% to 500%)
    zoomLevel = Math.max(50, Math.min(500, zoomLevel));
  }
  
  console.log(`Auto-layout: center (${centerLat.toFixed(2)}°, ${centerLong.toFixed(2)}°), zoom ${zoomLevel.toFixed(0)}%`);

  // Calculate pixel offsets to center the view
  // These offsets position the view within the canvas
  const pixelOffsetX = 0;
  const pixelOffsetY = 0;

  return {
    centerLat,
    centerLong,
    zoomLevel,
    pixelOffsetX,
    pixelOffsetY,
  };
}

/**
 * Check if auto-layout should be re-executed
 * 
 * This should return true when:
 * - Location list changes (locations added/removed)
 * - Physical map size changes
 * - Orientation changes
 * 
 * @param previousLocations - Previous location list
 * @param currentLocations - Current location list
 * @param previousSize - Previous physical size
 * @param currentSize - Current physical size
 * @returns true if auto-layout should be re-executed
 */
export function shouldRecalculateAutoLayout(
  previousLocations: Location[],
  currentLocations: Location[],
  previousSize: PhysicalSize,
  currentSize: PhysicalSize
): boolean {
  // Check if location count changed
  if (previousLocations.length !== currentLocations.length) {
    return true;
  }

  // Check if any location coordinates changed
  for (let i = 0; i < previousLocations.length; i++) {
    if (
      previousLocations[i].lat !== currentLocations[i].lat ||
      previousLocations[i].long !== currentLocations[i].long
    ) {
      return true;
    }
  }

  // Check if physical size changed
  if (
    previousSize.width !== currentSize.width ||
    previousSize.height !== currentSize.height
  ) {
    return true;
  }

  return false;
}
